import React, { useEffect, useState } from 'react';
import { CloudOff, CloudUpload, CheckCircle2, RefreshCw } from 'lucide-react';

/**
 * OfflineBanner
 * - Shows a top sticky banner when the browser is offline.
 * - Counts queued mutations (badge "X actions en attente").
 * - Toasts a sync confirmation once the SW flushes the queue.
 */
export const OfflineBanner = () => {
    const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
    const [queued, setQueued] = useState(0);
    const [syncing, setSyncing] = useState(false);
    const [justSynced, setJustSynced] = useState(null);

    useEffect(() => {
        const goOnline = () => {
            setOnline(true);
            // Trigger a manual replay and show "sync en cours" feedback
            if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                setSyncing(true);
                navigator.serviceWorker.controller.postMessage({ type: 'REPLAY_QUEUE' });
                // Auto-clear after 8s if no SYNC_FLUSHED received
                setTimeout(() => setSyncing(false), 8000);
            }
        };
        const goOffline = () => setOnline(false);
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);

        const onSwMessage = (event) => {
            const data = event.data || {};
            if (data.type === 'REQUEST_QUEUED') {
                setQueued((c) => c + 1);
            }
            if (data.type === 'SYNC_FLUSHED') {
                setQueued(Math.max(0, data.remaining || 0));
                setSyncing(false);
                if (data.success > 0) {
                    setJustSynced({ count: data.success });
                    setTimeout(() => setJustSynced(null), 4500);
                }
            }
        };
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', onSwMessage);
        }
        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.removeEventListener('message', onSwMessage);
            }
        };
    }, []);

    // Priority order: sync success > sync in progress > offline > pending queue
    if (justSynced) {
        return (
            <div
                className="fixed top-2 left-1/2 -translate-x-1/2 z-[100] bg-[#16A34A] text-white px-4 py-2 rounded-sm shadow-lg flex items-center gap-2 text-sm animate-in slide-in-from-top duration-300"
                data-testid="offline-sync-banner"
            >
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-semibold">{justSynced.count} action{justSynced.count > 1 ? 's' : ''} synchronisée{justSynced.count > 1 ? 's' : ''}</span>
            </div>
        );
    }

    if (syncing) {
        return (
            <div
                className="fixed top-0 inset-x-0 z-[100] bg-gradient-to-r from-[#0284C7] to-[#0EA5E9] text-white px-3 py-2 text-xs sm:text-sm flex items-center justify-center gap-2 shadow-md"
                data-testid="offline-syncing-banner"
            >
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span className="font-semibold">Synchronisation en cours…</span>
                {queued > 0 && (
                    <span className="bg-white/25 px-2 py-0.5 rounded-sm font-medium">
                        {queued} en file
                    </span>
                )}
            </div>
        );
    }

    if (!online) {
        return (
            <div
                className="fixed top-0 inset-x-0 z-[100] bg-[#DC2626] text-white px-3 py-1.5 text-xs sm:text-sm flex items-center justify-center gap-2"
                data-testid="offline-banner"
            >
                <CloudOff className="w-4 h-4" />
                <span>Hors ligne — vos modifications seront synchronisées au retour réseau.</span>
                {queued > 0 && (
                    <span className="bg-white/20 px-2 py-0.5 rounded-sm font-semibold">{queued} en attente</span>
                )}
            </div>
        );
    }

    // Online but pending queue not yet synced (e.g. browser doesn't fire SyncEvent)
    if (queued > 0) {
        return (
            <div
                className="fixed top-0 inset-x-0 z-[100] bg-[#D97706] text-white px-3 py-1.5 text-xs sm:text-sm flex items-center justify-center gap-2"
                data-testid="offline-pending-banner"
            >
                <CloudUpload className="w-4 h-4" />
                <span>{queued} action{queued > 1 ? 's' : ''} en attente — synchronisation…</span>
            </div>
        );
    }

    return null;
};
